'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { PaymentMethodMutationResponse } from '@sola/schema/dotcom/proxy-api/paymentMethods.js';
import { PaymentMethodTypesEnum } from '@sola/schema/dotcom/supporters/paymentMethods.js';
import type { SupporterPaymentMethodDetails } from '@sola/schema/dotcom/supporters/paymentMethods.js';
import type { GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { encryptCreditCardData } from '@sola/shared-utils/credit-cards.js';
import type { UserError } from '@sola/shared-utils/errors-user.js';
import { userErrorPaymentMethodUpdate } from '@sola/shared-utils/errors/supporters.js';
import { isUserError } from '@sola/shared-utils/httpClient.js';
import { MUTATION_KEYS, QUERY_KEYS } from '@sola/supporter-sdk/constants.js';
import {
  useReadyForQueries,
  useSupporterMutation,
} from '@sola/supporter-sdk/hooks/base.js';

import { updateCommitmentCache } from '../commitments/hooks';
import {
  PaymentMethodMutationError,
  addSupporterPaymentMethod,
  removeSupporterPaymentMethod,
  updateSupporterPaymentMethod,
} from './requests';

export type CreatePaymentMethodMutationResult = {
  paymentMethod: GraphQLSupporterPaymentMethod;
  commitmentUpdates?: PaymentMethodMutationResponse['commitmentUpdates'];
  failedCommitmentUpdateCount: number;
  success?: boolean;
  isPartialSuccess: boolean;
};

const isUserErrorArray = (error: unknown): error is UserError[] =>
  Array.isArray(error) && error.every(isUserError);

/**
 * Successful object to pass back to call site.
 */
const createPaymentMethodMutationResult = (
  response: PaymentMethodMutationResponse,
): CreatePaymentMethodMutationResult => {
  const failedCommitmentUpdateCount =
    response.commitmentUpdates?.failed.length || 0;

  return {
    paymentMethod: response.paymentMethod,
    commitmentUpdates: response.commitmentUpdates,
    failedCommitmentUpdateCount,
    success: response.success,
    isPartialSuccess: failedCommitmentUpdateCount > 0,
  };
};

type PaymentMethodAddMutationVariables = {
  paymentMethod: SupporterPaymentMethodDetails;
  commitmentIds?: string[];
};

/**
 * Hook to add a new payment method.
 */
export const usePaymentMethodAdd = () => {
  const queryClient = useQueryClient();
  const { globalId } = useReadyForQueries();

  return useSupporterMutation<
    CreatePaymentMethodMutationResult,
    PaymentMethodMutationError,
    PaymentMethodAddMutationVariables
  >({
    mutationFn: async ({ paymentMethod, commitmentIds }) => {
      const paymentMethodDetails = { ...paymentMethod };

      // Should probably be reusable.
      if (paymentMethodDetails.type === PaymentMethodTypesEnum.Card) {
        const { cardNumber, cvv } = paymentMethodDetails;
        const { creditCardEncrypted, cvvEncrypted } = encryptCreditCardData(
          { cvv, creditCard: cardNumber },
          !!cvv,
          window.TxEncrypt,
          process.env.NEXT_PUBLIC_TOKENEX_ENCRYPTION_KEY as string,
        );

        paymentMethodDetails.cardNumber = creditCardEncrypted ?? '';
        paymentMethodDetails.cvv = cvvEncrypted ?? '';
      }

      const response = await addSupporterPaymentMethod({
        globalId: globalId as string,
        paymentMethodDetails,
        commitmentIds,
      });

      if (isUserError(response) || isUserErrorArray(response)) {
        throw response;
      }

      // if (!response?.paymentMethod) {
      //   throw userErrorPaymentMethodCreate();
      // }

      return createPaymentMethodMutationResult(response);
    },
    onSuccess: (result: CreatePaymentMethodMutationResult) => {
      updateCommitmentCache(queryClient, result.commitmentUpdates);

      const { paymentMethod } = result;

      if (!paymentMethod.id) {
        return;
      }

      queryClient.setQueryData(
        [QUERY_KEYS.supporterPaymentMethods],
        (currentPaymentMethods: GraphQLSupporterPaymentMethod[] = []) => [
          ...currentPaymentMethods,
          paymentMethod,
        ],
      );
    },
  });
};

type PaymentMethodUpdateMutationVariables = {
  paymentMethod: GraphQLSupporterPaymentMethod;
  commitmentIds?: string[];
};

/**
 * Hook to update a payment method.
 */
export const usePaymentMethodUpdate = () => {
  const queryClient = useQueryClient();
  const { globalId } = useReadyForQueries();

  return useMutation<
    CreatePaymentMethodMutationResult,
    PaymentMethodMutationError,
    PaymentMethodUpdateMutationVariables
  >({
    mutationFn: async ({ paymentMethod, commitmentIds }) => {
      const response = await updateSupporterPaymentMethod({
        globalId: globalId as string,
        paymentMethod,
        commitmentIds,
      });

      if (isUserError(response) || isUserErrorArray(response)) {
        throw response;
      }

      if (!response?.paymentMethod) {
        throw userErrorPaymentMethodUpdate();
      }

      return createPaymentMethodMutationResult(response);
    },
    onSuccess: (result: CreatePaymentMethodMutationResult) => {
      updateCommitmentCache(queryClient, result.commitmentUpdates);

      const { paymentMethod } = result;

      if (!paymentMethod.id) {
        return;
      }

      queryClient.setQueryData(
        [QUERY_KEYS.supporterPaymentMethods],
        (currentPaymentMethods: GraphQLSupporterPaymentMethod[] = []) => {
          const paymentMethodIndex = currentPaymentMethods.findIndex(
            (existingPaymentMethod) =>
              existingPaymentMethod.id === paymentMethod.id,
          );

          const updatedMethods = [...currentPaymentMethods];
          updatedMethods[paymentMethodIndex] = paymentMethod;
          return updatedMethods;
        },
      );
    },
  });
};

/**
 * Hook to remove a payment method.
 */
export const usePaymentMethodRemove = (paymentMethodIdKey?: string) => {
  const queryClient = useQueryClient();
  const { globalId } = useReadyForQueries();

  return useSupporterMutation({
    mutationKey: [MUTATION_KEYS.paymentMethodRemove, paymentMethodIdKey],
    mutationFn: async (newPaymentMethodId?: string) => {
      const response = await removeSupporterPaymentMethod(
        globalId,
        paymentMethodIdKey,
        newPaymentMethodId,
      );

      return createPaymentMethodMutationResult(response);
    },
    onSuccess: (res, _newPaymentMethodId) => {
      const { success, commitmentUpdates } = res;

      if (success && !commitmentUpdates?.failed?.length) {
        queryClient.setQueryData(
          [QUERY_KEYS.supporterPaymentMethods],
          (paymentMethods: GraphQLSupporterPaymentMethod[]) =>
            paymentMethods && paymentMethodIdKey
              ? paymentMethods?.filter(
                  (paymentMethod) => paymentMethod?.id !== paymentMethodIdKey,
                )
              : (paymentMethods ?? []),
        );
      }

      // TODO: Do we want to present anything related to the failed commitments?
      if (commitmentUpdates?.updated?.length) {
        updateCommitmentCache(queryClient, commitmentUpdates);
      }
    },
  });
};
